import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { BrainCircuit, ClipboardList, Compass, Heart, Route, Plane, ChevronRight, ChevronDown, Globe, MapPin, BarChart3 } from "lucide-react";

const Landing = () => {
  const navigate = useNavigate();

  return (
    <div className="bg-gradient-to-br from-background via-background to-muted relative overflow-hidden">
      {/* Hero Section */}
      <main className="container mx-auto px-3 sm:px-4 py-12 sm:py-24 relative">
        <div className="max-w-4xl mx-auto">
          {/* Hero Card Container */}
          <div className="text-center space-y-6 sm:space-y-8 p-5 sm:p-8 md:p-12 rounded-3xl bg-card/30 border border-border/50 backdrop-blur-sm">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-accent/10 text-accent border border-accent/30">
              <BrainCircuit className="h-4 w-4 rotate-90" />
              <span className="text-xs sm:text-sm font-semibold tracking-wide uppercase">AI-Powered Travel Decisions</span>
            </div>

            {/* Headline — dramatic sizing */}
            <h1 className="text-3xl sm:text-5xl md:text-7xl leading-tight" style={{ letterSpacing: '-0.02em' }}>
              <span className="font-normal">Let's Decide Your Next</span>{" "}
              <span className="font-bold text-accent">
                Destination
              </span>
            </h1>

            {/* Subheadline — lighter weight for contrast */}
            <p className="text-lg sm:text-xl md:text-2xl text-muted-foreground max-w-2xl mx-auto font-light">
              Quick quiz. Personalized picks. Real prices. Go.
            </p>

            {/* CTA Button */}
            <div className="pt-2 sm:pt-4">
              <Button 
                variant="hero" 
                size="lg"
                onClick={() => navigate('/quiz')}
                className="text-base sm:text-lg px-8 sm:px-12 py-5 sm:py-6 h-auto"
              >
                Start Your Journey
              </Button>
            </div>
          </div>

      {/* Journey Flow */}
      <div className="pt-20 pb-8 max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold mb-3 tracking-tight">How It Works</h2>
          <p className="text-muted-foreground text-base sm:text-lg font-light">Your journey to the perfect trip in 5 simple steps</p>
        </div>

        {/* Desktop: Horizontal Flow */}
        <div className="hidden lg:flex items-start justify-center gap-4">
          {[
            { icon: ClipboardList, title: "Take Quiz", desc: "Answer quick questions about your travel style", step: 1 },
            { icon: Compass, title: "Get Results", desc: "Discover personalized destinations with real-time pricing", step: 2 },
            { icon: Heart, title: "Save Favorites", desc: "Save your best picks to revisit anytime", step: 3 },
            { icon: Route, title: "Plan Itinerary", desc: "Generate day-by-day travel plans", step: 4 },
            { icon: Plane, title: "Book Trip", desc: "One-click links to flights and hotels", step: 5 },
          ].map((item, index) => (
            <div key={item.step} className="flex items-center" style={{ animationDelay: `${index * 100}ms` }}>
              <div className="flex flex-col items-center text-center w-40 group">
                <div className="relative">
                  <div className="w-20 h-20 rounded-2xl bg-accent/10 flex items-center justify-center border border-accent/30 shadow-lg shadow-accent/10 group-hover:bg-accent/20 group-hover:scale-110 group-hover:border-accent/60 transition-all duration-300">
                    <item.icon className="h-8 w-8 text-accent group-hover:scale-110 transition-transform duration-300" />
                  </div>
                  <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-accent text-accent-foreground text-sm font-bold flex items-center justify-center shadow-md">
                    {item.step}
                  </span>
                </div>
                <h3 className="font-bold mt-4 text-base">{item.title}</h3>
                <p className="text-muted-foreground text-sm mt-2 leading-relaxed px-2">{item.desc}</p>
              </div>
              {index < 4 && (
                <div className="flex items-center mx-3" style={{ marginTop: '-2rem' }}>
                  <div className="w-6 h-0.5 bg-gradient-to-r from-accent/40 to-accent/60 rounded-full"></div>
                  <ChevronRight className="h-6 w-6 text-accent -ml-1.5" strokeWidth={2.5} />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Mobile/Tablet: Vertical Flow */}
        <div className="lg:hidden flex flex-col items-center gap-6 max-w-md mx-auto">
          {[
            { icon: ClipboardList, title: "Take Quiz", desc: "Answer quick questions about your travel style", step: 1 },
            { icon: Compass, title: "Get Results", desc: "Discover personalized destinations with real-time pricing", step: 2 },
            { icon: Heart, title: "Save Favorites", desc: "Save your best picks to revisit anytime", step: 3 },
            { icon: Route, title: "Plan Itinerary", desc: "Generate day-by-day travel plans", step: 4 },
            { icon: Plane, title: "Book Trip", desc: "One-click links to flights and hotels", step: 5 },
          ].map((item, index) => (
            <div key={item.step} className="flex flex-col items-center w-full">
              <div className="flex items-center gap-5 p-5 rounded-2xl bg-card/80 border border-border/50 w-full hover:border-accent/50 hover:shadow-elevated transition-all duration-300">
                <div className="relative flex-shrink-0">
                  <div className="w-16 h-16 rounded-xl bg-accent/10 flex items-center justify-center border border-accent/30">
                    <item.icon className="h-7 w-7 text-accent" />
                  </div>
                  <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-accent text-accent-foreground text-sm font-bold flex items-center justify-center">
                    {item.step}
                  </span>
                </div>
                <div className="text-left flex-1 min-w-0">
                  <h3 className="font-bold text-base">{item.title}</h3>
                  <p className="text-muted-foreground text-sm mt-1 leading-relaxed">{item.desc}</p>
                </div>
              </div>
              {index < 4 && (
                <div className="flex flex-col items-center py-2">
                  <div className="w-0.5 h-4 bg-gradient-to-b from-accent/40 to-accent/60 rounded-full"></div>
                  <ChevronDown className="h-6 w-6 text-accent -mt-1" strokeWidth={2.5} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
        </div>
      </main>

      {/* Track Your Adventures Section */}
      <section className="py-12 sm:py-20 bg-muted/30">
        <div className="container mx-auto px-3 sm:px-4">
          <div className="max-w-4xl mx-auto text-center space-y-4 mb-8 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">
              Track Your{" "}
              <span className="text-accent">
                Adventures
              </span>
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto font-light">
              Record every trip, watch your world map fill up, and explore your travel stats
            </p>
          </div>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 max-w-5xl mx-auto">
            {[
              {
                icon: Globe,
                title: "Complete Your Map",
                desc: "Add places you've visited and watch countries light up on your personal world map",
              },
              {
                icon: MapPin,
                title: "Travel Records",
                desc: "Log every trip with dates, ratings, and notes — your travel diary, all in one place",
              },
              {
                icon: BarChart3,
                title: "Your Stats",
                desc: "See your travel patterns — favorite climates, trip styles, countries explored, and more",
              },
            ].map((feature) => (
              <div
                key={feature.title}
                className="group p-6 rounded-2xl bg-card/50 border border-border/50 hover:border-accent/50 hover:bg-card/80 hover:shadow-accent hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-14 h-14 rounded-2xl bg-accent/10 flex items-center justify-center border border-accent/30 mb-4 group-hover:scale-110 group-hover:bg-accent/20 transition-all duration-300">
                  <feature.icon className="h-7 w-7 text-accent" />
                </div>
                <h3 className="font-bold text-lg mb-2">{feature.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Landing;
